/**
 * Single backend host for CRM `/api`, IAM `/user`, audit log `/api/audit-logs`, and `/websocket`.
 *
 * Set `BACKEND_URL` in `.env` (default: https://crm-backend.naqdpay.af).
 * Local dev: `USE_DEV_PROXY=1` so the browser uses same-origin paths and webpack proxies to `BACKEND_URL`.
 */
import { BUILD_ENV, IS_PRODUCTION_BUILD } from './build-env';
export const DEFAULT_BACKEND_URL = 'https://crm-backend.naqdpay.af';
function envFlagIsTrue(value) {
    return value === '1' || value.toLowerCase() === 'true';
}
/** Normalize host with optional scheme (`backendcallcenter.azizibank.af` → `https://…`). */
export function normalizeBackendUrl(raw) {
    const trimmed = raw.trim().replace(/\/$/, '');
    if (!trimmed || trimmed === 'relative' || trimmed === '/') {
        return '';
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    return `https://${trimmed}`;
}
export function configuredBackendUrlRaw() {
    return BUILD_ENV.BACKEND_URL;
}
function isWebpackServe() {
    return BUILD_ENV.WEBPACK_SERVE === '1' || BUILD_ENV.WEBPACK_SERVE.toLowerCase() === 'true';
}
/**
 * When true, axios uses relative `/api`, `/user`, … and webpack dev-server proxies to {@link resolveBackendProxyTarget}.
 */
export function usesDevProxy() {
    const flag = BUILD_ENV.USE_DEV_PROXY;
    if (envFlagIsTrue(flag)) {
        return true;
    }
    if (flag === '0' || flag.toLowerCase() === 'false') {
        return false;
    }
    const hasExplicitOrigin = Boolean(normalizeBackendUrl(configuredBackendUrlRaw()));
    if (hasExplicitOrigin) {
        return false;
    }
    return !IS_PRODUCTION_BUILD || isWebpackServe();
}
/** Absolute origin for browser API calls, or empty when using same-origin dev proxy. */
export function getBackendOrigin() {
    const explicit = normalizeBackendUrl(configuredBackendUrlRaw());
    if (usesDevProxy()) {
        return '';
    }
    return explicit || DEFAULT_BACKEND_URL;
}
/** Webpack dev-server proxy target (always absolute). */
export function resolveBackendProxyTarget() {
    return normalizeBackendUrl(configuredBackendUrlRaw()) || DEFAULT_BACKEND_URL;
}
/** Build a backend URL (`/api/...`, `/user/...`, …). */
export function backendUrl(pathFromSlash) {
    const rel = pathFromSlash.startsWith('/') ? pathFromSlash : `/${pathFromSlash}`;
    const origin = getBackendOrigin();
    return origin ? `${origin}${rel}` : rel;
}
